<template>
  <div class="landing-page">
    <!-- Ambient Background -->
    <div class="ambient-bg">
      <div class="gradient-orb orb-1"></div>
      <div class="gradient-orb orb-2"></div>
      <div class="gradient-orb orb-3"></div>
      <div class="noise-overlay"></div>
    </div>

    <!-- Header -->
    <header class="header" :class="{ 'scrolled': isScrolled }">
      <div class="header-content">
        <div class="logo">
          <div class="logo-mark">
            <img src="/imgs/LOGO.png" />
          </div>
          <span class="logo-text">BODA</span>
        </div>
        <nav class="nav-menu">
          <a href="#service" class="nav-link">서비스</a>
          <a href="#features" class="nav-link">기능</a>
          <a href="#company" class="nav-link">회사소개</a>
          <a href="/login" class="nav-link nav-login">로그인</a>
        </nav>
        <a href="/login" class="cta-header">
          <span>시작하기</span>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M3 8h10m0 0L9 4m4 4l-4 4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </a>
      </div>
    </header>

    <!-- Hero Section -->
    <section class="hero">
      <!-- Hero Background Image -->
      <div class="hero-bg">
        <img :src="heroBgImage" alt="" class="hero-bg-image" />
        <div class="hero-bg-overlay"></div>
      </div>
      
      <div class="hero-container">
        <!-- Left Content -->
        <div class="hero-content">
          <div class="hero-badge">
            <span class="badge-dot"></span>
            <span>{{ activeUsers.toLocaleString() }}명의 설계사가 함께합니다</span>
          </div>
          
          <h1 class="hero-title">
            <span class="title-line">혼자 뛴</span>
            <span class="title-line title-highlight">나의 영업</span>
            <span class="title-line">이제 <b class="gradient-text">AI</b> 함께.</span>
          </h1>
          
          <p class="hero-description">
            고객 관리부터 보장 분석까지, 설계사에게 필요한 모든 것을<br/>
            하나의 플랫폼에서 경험하세요.
          </p>
          
          <div class="hero-actions">
            <a href="/signup" class="btn-primary">
              <span>무료로 시작하기</span>
              <div class="btn-shine"></div>
            </a>
            <a href="#features" class="btn-secondary">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <circle cx="10" cy="10" r="9" stroke="currentColor" stroke-width="1.5"/>
                <path d="M8 7l4 3-4 3V7z" fill="currentColor"/>
              </svg>
              <span>기능 둘러보기</span>
            </a>
          </div>
          
          <div class="hero-stats">
            <div class="stat-item">
              <span class="stat-number">98%</span>
              <span class="stat-label">고객 만족도</span>
            </div>
            <div class="stat-divider"></div>
            <div class="stat-item">
              <span class="stat-number">2.5배</span>
              <span class="stat-label">영업 효율 증가</span>
            </div>
            <div class="stat-divider"></div>
            <div class="stat-item">
              <span class="stat-number">6.3시간</span>
              <span class="stat-label">일일 시간 절약</span>
            </div>
          </div>
        </div>
        
        <!-- Right Visual -->
        <div class="hero-visual">
          <div class="visual-frame">
            <div class="frame-glow"></div>
            <div class="app-preview">
              <div class="preview-header">
                <div class="preview-dots">
                  <span></span><span></span><span></span>
                </div>
                <span class="preview-title">BODA Dashboard</span>
              </div>
              <div class="preview-content">
                <div class="preview-sidebar">
                  <div class="sidebar-item active">
                    <span class="sidebar-icon">👥</span>
                  </div>
                  <div class="sidebar-item">
                    <span class="sidebar-icon">📊</span>
                  </div>
                  <div class="sidebar-item">
                    <span class="sidebar-icon">💡</span>
                  </div>
                  <div class="sidebar-item">
                    <span class="sidebar-icon">💰</span>
                  </div>
                </div>
                <div class="preview-main">
                  <div class="preview-card card-1">
                    <div class="card-header">
                      <span class="card-title">이번 달 신규 고객</span>
                      <span class="card-badge">+12%</span>
                    </div>
                    <span class="card-value">24명</span>
                  </div>
                  <div class="preview-card card-2">
                    <div class="card-header">
                      <span class="card-title">보장 분석 완료</span>
                    </div>
                    <span class="card-value">156건</span>
                  </div>
                  <div class="preview-card card-3">
                    <div class="card-header">
                      <span class="card-title">예정된 상담</span>
                    </div>
                    <div class="card-list">
                      <div class="list-item">
                        <span class="item-dot"></span>
                        <span>김철수 · 오후 2:00</span>
                      </div>
                      <div class="list-item">
                        <span class="item-dot"></span>
                        <span>이영희 · 오후 4:30</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="floating-card floating-1">
              <span class="floating-icon">✓</span>
              <span class="floating-text">계약 체결 완료</span>
            </div>
            <div class="floating-card floating-2">
              <span class="floating-icon">🔔</span>
              <span class="floating-text">새 상담 요청</span>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Scroll Indicator -->
      <div class="scroll-indicator">
        <div class="scroll-mouse">
          <div class="scroll-wheel"></div>
        </div>
        <span>스크롤하여 더 알아보기</span>
      </div>
    </section>

    <!-- Values Section -->
    <section id="service" class="values-section">
      <div class="section-container">
        <div class="section-header">
          <span class="section-label">WHY BODA</span>
          <h2 class="section-title">왜 보다(BODA)인가?</h2>
          <p class="section-subtitle">설계사의 진짜 문제를 해결합니다</p>
        </div>
        
        <div class="values-grid">
          <div class="value-card" v-for="(value, index) in coreValues" :key="index">
            <div class="card-number">{{ String(index + 1).padStart(2, '0') }}</div>
            <div class="card-icon">{{ value.icon }}</div>
            <h3 class="card-title">{{ value.title }}</h3>
            <p class="card-description">{{ value.description }}</p>
            <div class="card-decoration"></div>
          </div>
        </div>
      </div>
    </section>

    <!-- Features Section -->
    <section id="features" class="features-section">
      <div class="section-container">
        <div class="section-header">
          <span class="section-label">FEATURES</span>
          <h2 class="section-title">모든 업무를 하나로</h2>
          <p class="section-subtitle">고객 관리부터 보장 분석까지, 필요한 모든 기능</p>
        </div>
        
        <div class="features-grid">
          <div class="feature-card" v-for="(feature, index) in features" :key="index">
            <div class="feature-visual">
              <div class="visual-bg"></div>
              <div class="visual-icon">{{ feature.icon }}</div>
            </div>
            <div class="feature-content">
              <span class="feature-number">{{ String(index + 1).padStart(2, '0') }}</span>
              <h3 class="feature-title">{{ feature.title }}</h3>
              <p class="feature-description">{{ feature.description }}</p>
              <ul class="feature-points">
                <li v-for="point in feature.points" :key="point">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M4 8h8" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                  </svg>
                  <span>{{ point }}</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Download Section -->
    <section class="download-section">
      <div class="download-container">
        <div class="download-visual">
          <div class="phone-mockup">
            <div class="phone-screen">
              <div class="screen-content">
                <div class="app-icon">
                  <svg viewBox="0 0 40 40" width="48" height="48">
                    <rect x="2" y="2" width="36" height="36" rx="10" fill="url(#logoGrad)" />
                    <path d="M12 20 L17 25 L28 14" stroke="white" stroke-width="3" fill="none" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </div>
                <span class="app-name">BODA</span>
                <span class="app-tagline">당신의 영업 파트너</span>
              </div>
            </div>
            <div class="phone-notch"></div>
          </div>
          <div class="download-decoration">
            <div class="deco-ring ring-1"></div>
            <div class="deco-ring ring-2"></div>
            <div class="deco-ring ring-3"></div>
          </div>
        </div>
        
        <div class="download-content">
          <span class="download-label">MOBILE APP</span>
          <h2 class="download-title">
            언제 어디서나<br/>
            <span class="highlight">BODA</span>와 함께
          </h2>
          <p class="download-description">
            이동 중에도 고객 정보를 확인하고, 상담 일정을 관리하세요.<br/>
            모바일 앱으로 더욱 스마트한 영업을 경험해보세요.
          </p>
          <div class="download-buttons">
            <a href="#" class="store-btn">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
              </svg>
              <div class="store-text">
                <span class="store-label">Download on the</span>
                <span class="store-name">App Store</span>
              </div>
            </a>
            <a href="#" class="store-btn">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                <path d="M3.609 1.814L13.792 12 3.61 22.186a.996.996 0 0 1-.61-.92V2.734a1 1 0 0 1 .609-.92zm10.89 10.893l2.302 2.302-10.937 6.333 8.635-8.635zm3.199-3.198l2.807 1.626a1 1 0 0 1 0 1.73l-2.808 1.626L15.206 12l2.492-2.491zM5.864 2.658L16.802 8.99l-2.303 2.303-8.635-8.635z"/>
              </svg>
              <div class="store-text">
                <span class="store-label">Get it on</span>
                <span class="store-name">Google Play</span>
              </div>
            </a>
          </div>
        </div>
      </div>
    </section>

    <!-- Expert Section -->
    <section id="company" class="expert-section">
      <div class="expert-container">
        <div class="expert-visual">
          <div class="expert-image-frame">
            <div class="image-placeholder">
              <div class="placeholder-icon">👔</div>
              <div class="placeholder-pattern"></div>
            </div>
          </div>
          <div class="expert-badge-float">
            <span class="badge-icon">🏆</span>
            <div class="badge-text">
              <span class="badge-number">10+</span>
              <span class="badge-label">년 경력</span>
            </div>
          </div>
        </div>
        
        <div class="expert-content">
          <span class="expert-label">ABOUT US</span>
          <h2 class="expert-title">
            보험 전문가들이 만드는<br/>
            <span class="highlight">보다(BODA)</span>
          </h2>
          <p class="expert-description">
            10년 이상의 보험 업계 경험을 가진 전문가들이<br/>
            현장의 목소리를 담아 만들었습니다.
          </p>
          <div class="expert-features">
            <div class="expert-feature">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"/>
                <path d="M8 12l3 3 5-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              <span>현장 검증된 기능</span>
            </div>
            <div class="expert-feature">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"/>
                <path d="M8 12l3 3 5-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              <span>지속적인 업데이트</span>
            </div>
            <div class="expert-feature">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"/>
                <path d="M8 12l3 3 5-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              <span>전문 고객 지원</span>
            </div>
          </div>
          <a href="#" class="expert-cta">
            <span>회사 소개 보기</span>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M4 10h12m0 0l-4-4m4 4l-4 4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </a>
        </div>
      </div>
    </section>

    <!-- Footer -->
    <footer class="footer">
      <div class="footer-container">
        <div class="footer-top">
          <div class="footer-brand">
            <div class="logo">
              <div class="logo-mark">
                <img src="/imgs/LOGO.png" />
              </div>
              <span class="logo-text">BODA</span>
            </div>
            <p class="brand-tagline">당신의 영업 성과를 바꾸는 첫 걸음</p>
          </div>
          
          <div class="footer-links">
            <div class="link-group">
              <h4>서비스</h4>
              <a href="#">고객 관리</a>
              <a href="#">보장 분석</a>
              <a href="#">플랜 제안</a>
              <a href="#">청구 관리</a>
            </div>
            <div class="link-group">
              <h4>회사</h4>
              <a href="#">회사 소개</a>
              <a href="#">채용</a>
              <a href="#">블로그</a>
              <a href="#">뉴스</a>
            </div>
            <div class="link-group">
              <h4>고객센터</h4>
              <a href="tel:041-435-7781">041-435-7781</a>
              <a href="#">이용 가이드</a>
              <a href="#">FAQ</a>
              <a href="#">제휴문의</a>
            </div>
          </div>
        </div>
        
        <div class="footer-info">
          <div class="company-info">
            <p>주식회사 브이에프티파트너 · 대표 김주헌 · 사업자등록번호 497-81-02588</p>
            <p>충청남도 논산시 대학로 97</p>
          </div>
        </div>
        
        <div class="footer-bottom">
          <p class="copyright">© 2024 BODA. All Rights Reserved</p>
          <div class="footer-legal">
            <a href="#">이용약관</a>
            <a href="#">개인정보처리방침</a>
          </div>
        </div>
      </div>
    </footer>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue';

const isScrolled = ref(false);
const activeUsers = ref(1247);

// Hero Background Image
const heroBgImage = ref('/imgs/common/intro_banner_1.PNG');

const handleScroll = () => {
  isScrolled.value = window.scrollY > 50;
};

onMounted(() => {
  window.addEventListener('scroll', handleScroll);
});

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll);
});

const coreValues = ref([
  {
    icon: '⚡',
    title: '스마트한 영업',
    description: '고객 관리, 보장 분석, 청구 추적 등 세일즈에 필요한 모든 기능이 한 곳에 있어요.'
  },
  {
    icon: '🎯',
    title: '쉬운 고객발굴',
    description: '자동 알림과 상담 이력으로 고객과의 접점을 놓치지 않고 새로운 영업 기회를 만들어요.'
  },
  {
    icon: '✓',
    title: '검증된 솔루션',
    description: '실제 현장에서 검증된 기능과 워크플로우로 바로 사용할 수 있어요.'
  }
]);

const features = ref([
  {
    icon: '👥',
    title: '고객 카드',
    description: '가족 구성, 상담 메모, 마지막 접촉일까지 한눈에 관리',
    points: [
      '가족 구성원 카드 뷰',
      '상담 메모 타임라인',
      '마지막 접촉일 자동 알림'
    ]
  },
  {
    icon: '📊',
    title: '보장 스케치',
    description: '복잡한 보험 보장을 챕터별로 구조화하여 한눈에 파악',
    points: [
      '챕터별 보장 트리 구조',
      '여러 플랜 비교 기능',
      '부족/중복 보장 표시'
    ]
  },
  {
    icon: '💡',
    title: '플랜 제안',
    description: '고객 맞춤 제안서를 즉시 생성하고 공유',
    points: [
      '보장 분석 결과 시각화',
      'PDF 다운로드 및 공유',
      '제안 이력 관리'
    ]
  },
  {
    icon: '💰',
    title: '청구 현황',
    description: '보험금 청구부터 결과까지 전 과정을 추적',
    points: [
      '청구 건 등록 및 상태 관리',
      '처리 단계별 알림',
      '과거 청구 이력 조회'
    ]
  }
]);
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800&display=swap');

:root {
  color-scheme: light dark;
}

* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

.landing-page {
  /* 랜딩 전용 추가 변수 (global.css에 없는 것만) */
  --lp-color-bg: light-dark(#FAFAFA, #0a0a0f);
  --lp-color-bg-elevated: light-dark(#FFFFFF, #18181B);
  --lp-color-bg-card: light-dark(#F4F4F5, #1F1F23);
  --lp-color-text-muted: light-dark(#52525B, #A1A1AA);
  --lp-color-text-secondary: light-dark(#3F3F46, #D4D4D8);
  --lp-color-border: light-dark(#E4E4E7, #27272A);
  --lp-color-border-light: light-dark(#D4D4D8, #3F3F46);
  
  font-family: var(--font-family-base);
  background: var(--lp-color-bg);
  color: var(--color-text-primary);
  line-height: 1.6;
  overflow-x: hidden;
}

/* Ambient Background */
.ambient-bg {
  position: fixed;
  inset: 0;
  pointer-events: none;
  z-index: 0;
  overflow: hidden;
}

.gradient-orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(100px);
  opacity: 0.4;
  animation: orbFloat 20s ease-in-out infinite;
}

.orb-1 {
  width: 600px;
  height: 600px;
  background: light-dark(
    radial-gradient(circle, rgba(14, 165, 233, 0.25) 0%, transparent 70%),
    radial-gradient(circle, rgba(245, 158, 11, 0.3) 0%, transparent 70%)
  );
  top: -200px;
  right: -100px;
  animation-delay: 0s;
}

.orb-2 {
  width: 500px;
  height: 500px;
  background: light-dark(
    radial-gradient(circle, rgba(245, 158, 11, 0.2) 0%, transparent 70%),
    radial-gradient(circle, rgba(217, 119, 6, 0.2) 0%, transparent 70%)
  );
  bottom: 20%;
  left: -150px;
  animation-delay: -7s;
}

.orb-3 {
  width: 400px;
  height: 400px;
  background: light-dark(
    radial-gradient(circle, rgba(56, 189, 248, 0.2) 0%, transparent 70%),
    radial-gradient(circle, rgba(252, 211, 77, 0.15) 0%, transparent 70%)
  );
  top: 50%;
  right: 20%;
  animation-delay: -14s;
}

@keyframes orbFloat {
  0%, 100% { transform: translate(0, 0) scale(1); }
  33% { transform: translate(30px, -30px) scale(1.05); }
  66% { transform: translate(-20px, 20px) scale(0.95); }
}

.noise-overlay {
  position: absolute;
  inset: 0;
  background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E");
  opacity: 0.03;
  mix-blend-mode: overlay;
}

/* Header */
.header {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  padding: 1rem 0;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.header.scrolled {
  background: light-dark(
    rgba(250, 250, 250, 0.9),
    rgba(9, 9, 11, 0.85)
  );
  backdrop-filter: blur(20px);
  border-bottom: 1px solid var(--lp-color-border);
}

.header-content {
  width:100%;
  max-width: 1200px;
  padding:0px 16px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-sizing: border-box;
}

.logo {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.logo img {
  width:36px;
  height:36px;
  object-fit:cover;
  border-radius: 50%;
}

.nav-menu {
  display: flex;
  align-items: center;
  gap: 2.5rem;
}

.nav-link {
  color: var(--lp-color-text-secondary);
  text-decoration: none;
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-medium);
  transition: color 0.3s;
  position: relative;
}

.nav-link::after {
  content: '';
  position: absolute;
  bottom: -4px;
  left: 0;
  width: 0;
  height: 2px;
  background: var(--color-brand);
  transition: width 0.3s;
}

.nav-link:hover {
  color: var(--color-text-primary);
}

.nav-link:hover::after {
  width: 100%;
}

.nav-login {
  opacity: 0.6;
}

.cta-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: var(--color-brand);
  color: var(--color-text-white);
  text-decoration: none;
  font-weight: var(--font-weight-semibold);
  font-size: var(--font-size-base);
  border-radius: 100px;
  transition: all 0.3s;
}

.cta-header:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 30px rgba(245, 158, 11, 0.3);
}

/* Hero Section */
.hero {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 8rem 2rem 4rem;
  position: relative;
  z-index: 1;
  overflow: hidden;
}

/* Hero Background Image */
.hero-bg {
  position: absolute;
  inset: 0;
  z-index: -1;
  overflow: hidden;
}

.hero-bg-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  filter: blur(8px);
  transform: scale(1.05);
}

.hero-bg-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    180deg,
    rgba(250, 250, 250, 0.85) 0%,
    rgba(250, 250, 250, 0.95) 100%
  );
}

@media (prefers-color-scheme: dark) {
  .hero-bg-overlay {
    background: linear-gradient(
      180deg,
      rgba(9, 9, 11, 0.75) 0%,
      rgba(9, 9, 11, 0.9) 100%
    );
  }
}


.hero-container {
  max-width: 1200px;
  width:100%;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: center;
}

.hero-content {
  max-width: 560px;
}

.hero-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.5rem 1rem 0.5rem 0.75rem;
  background: var(--lp-color-bg-elevated);
  border: 1px solid var(--lp-color-border);
  border-radius: 100px;
  font-size: var(--font-size-sm);
  color: var(--lp-color-text-secondary);
  margin-bottom: 2rem;
}

.badge-dot {
  width: 8px;
  height: 8px;
  background: #22C55E;
  border-radius: 50%;
  animation: pulse 2s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.5; transform: scale(1.2); }
}

.hero-title {
  font-size: var(--font-size-4xl);
  font-weight: var(--font-weight-extrabold);
  line-height: 1.1;
  letter-spacing: -0.03em;
  margin-bottom: 1.5rem;
}

.title-line {
  display: block;
}

.title-highlight {
  background: linear-gradient(135deg, var(--color-brand) 0%, var(--color-brand-light) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.hero-description {
  font-size: var(--font-size-md);
  color: var(--lp-color-text-secondary);
  line-height: 1.8;
  margin-bottom: 2.5rem;
}

.hero-actions {
  display: flex;
  gap: 1rem;
  margin-bottom: 3rem;
}

.btn-primary {
  position: relative;
  display: inline-flex;
  align-items: center;
  padding: 1rem 2rem;
  background: linear-gradient(135deg, var(--color-brand) 0%, var(--color-brand) 100%);
  color: var(--lp-color-bg);
  text-decoration: none;
  font-weight: var(--font-weight-semibold);
  font-size: var(--font-size-base);
  border-radius: var(--radius-md);
  overflow: hidden;
  transition: all 0.3s;
}

.btn-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 40px rgba(245, 158, 11, 0.4);
}

.btn-shine {
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent);
  animation: shine 3s ease-in-out infinite;
}

@keyframes shine {
  0% { left: -100%; }
  50%, 100% { left: 100%; }
}

.btn-secondary {
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 1.5rem;
  background: transparent;
  color: var(--color-text-primary);
  text-decoration: none;
  font-weight: var(--font-weight-medium);
  font-size: var(--font-size-base);
  border: 1px solid var(--lp-color-border);
  border-radius: var(--radius-md);
  transition: all 0.3s;
}

.btn-secondary:hover {
  background: var(--lp-color-bg-elevated);
  border-color: var(--lp-color-border-light);
}

.hero-stats {
  display: flex;
  align-items: center;
  gap: 2rem;
  padding: 1.5rem 0;
  border-top: 1px solid var(--lp-color-border);
}

.stat-item {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.stat-number {
  font-family: 'Outfit', sans-serif;
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-primary);
}

.stat-label {
  font-size: var(--font-size-sm);
  color: var(--lp-color-text-muted);
}

.stat-divider {
  width: 1px;
  height: 40px;
  background: var(--lp-color-border);
}

/* Hero Visual */
.hero-visual {
  position: relative;
}

.visual-frame {
  position: relative;
  perspective: 1000px;
}

.frame-glow {
  position: absolute;
  inset: -20%;
  background: radial-gradient(ellipse at center, rgba(245, 158, 11, 0.15) 0%, transparent 70%);
  filter: blur(40px);
  animation: glowPulse 4s ease-in-out infinite;
}

@keyframes glowPulse {
  0%, 100% { opacity: 0.5; }
  50% { opacity: 0.8; }
}

.app-preview {
  background: var(--lp-color-bg-card);
  border: 1px solid var(--lp-color-border);
  border-radius: var(--radius-lg);
  overflow: hidden;
  box-shadow: light-dark(
    0 20px 60px rgba(0, 0, 0, 0.1),
    0 20px 60px rgba(0, 0, 0, 0.5)
  );
  transform: rotateY(-5deg) rotateX(5deg);
  transition: transform 0.6s cubic-bezier(0.4, 0, 0.2, 1);
}

.app-preview:hover {
  transform: rotateY(0deg) rotateX(0deg);
}

.preview-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem 1.25rem;
  background: var(--lp-color-bg-elevated);
  border-bottom: 1px solid var(--lp-color-border);
}

.preview-dots {
  display: flex;
  gap: 6px;
}

.preview-dots span {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: var(--lp-color-border-light);
}

.preview-dots span:first-child { background: #EF4444; }
.preview-dots span:nth-child(2) { background: #F59E0B; }
.preview-dots span:last-child { background: #22C55E; }

.preview-title {
  font-size: var(--font-size-sm);
  color: var(--lp-color-text-muted);
}

.preview-content {
  display: flex;
  min-height: 320px;
}

.preview-sidebar {
  width: 60px;
  background: var(--lp-color-bg-elevated);
  border-right: 1px solid var(--lp-color-border);
  padding: 1rem 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
}

.sidebar-item {
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--radius-md);
  transition: all 0.3s;
}

.sidebar-item.active {
  background: rgba(245, 158, 11, 0.15);
}

.sidebar-icon {
  font-size: var(--font-size-md);
}

.preview-main {
  flex: 1;
  padding: 1.25rem;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}

.preview-card {
  background: var(--lp-color-bg-elevated);
  border: 1px solid var(--lp-color-border);
  border-radius: var(--radius-md);
  padding: 1rem;
}

.card-3 {
  grid-column: span 2;
}

.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.75rem;
}

.card-title {
  font-size: var(--font-size-xs);
  color: var(--lp-color-text-muted);
}

.card-badge {
  font-size: var(--font-size-xs);
  padding: 0.2rem 0.5rem;
  background: rgba(34, 197, 94, 0.15);
  color: #22C55E;
  border-radius: var(--radius-sm);
  font-weight: var(--font-weight-semibold);
}

.card-value {
  font-family: 'Outfit', sans-serif;
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-bold);
}

.card-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.list-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: var(--font-size-sm);
  color: var(--lp-color-text-secondary);
}

.item-dot {
  width: 6px;
  height: 6px;
  background: var(--color-brand);
  border-radius: 50%;
}

/* Floating Cards */
.floating-card {
  position: absolute;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  background: var(--lp-color-bg-elevated);
  border: 1px solid var(--lp-color-border);
  border-radius: var(--radius-md);
  box-shadow: light-dark(
    0 10px 40px rgba(0, 0, 0, 0.1),
    0 10px 40px rgba(0, 0, 0, 0.3)
  );
  animation: floatCard 6s ease-in-out infinite;
}

.floating-1 {
  top: 10%;
  right: -20px;
  animation-delay: 0s;
}

.floating-2 {
  bottom: 15%;
  left: -30px;
  animation-delay: -3s;
}

@keyframes floatCard {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-10px); }
}

.floating-icon {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(245, 158, 11, 0.15);
  border-radius: var(--radius-md);
  font-size: var(--font-size-base);
}

.floating-text {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
}

/* Scroll Indicator */
.scroll-indicator {
  position: absolute;
  bottom: 3rem;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  color: var(--lp-color-text-muted);
  font-size: var(--font-size-xs);
  animation: fadeInUp 1s ease-out 1s both;
}

@keyframes fadeInUp {
  from { opacity: 0; transform: translate(-50%, 20px); }
  to { opacity: 1; transform: translate(-50%, 0); }
}

.scroll-mouse {
  width: 24px;
  height: 40px;
  border: 2px solid var(--lp-color-border-light);
  border-radius: var(--radius-md);
  position: relative;
}

.scroll-wheel {
  width: 4px;
  height: 8px;
  background: var(--lp-color-text-muted);
  border-radius: 2px;
  position: absolute;
  top: 8px;
  left: 50%;
  transform: translateX(-50%);
  animation: scrollWheel 2s ease-in-out infinite;
}

@keyframes scrollWheel {
  0%, 100% { top: 8px; opacity: 1; }
  50% { top: 20px; opacity: 0.3; }
}

/* Values Section */
.values-section {
  padding: 8rem 2rem;
  position: relative;
  z-index: 1;
}

.section-container {
  max-width: 1200px;
  margin: 0 auto;
}

.section-header {
  text-align: center;
  margin-bottom: 5rem;
}

.section-label {
  display: inline-block;
  font-family: 'Outfit', sans-serif;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  letter-spacing: 0.2em;
  color: var(--color-brand);
  margin-bottom: 1rem;
}

.section-title {
  font-size: var(--font-size-3xl);
  font-weight: var(--font-weight-extrabold);
  letter-spacing: -0.02em;
  margin-bottom: 1rem;
}

.section-subtitle {
  font-size: var(--font-size-md);
  color: var(--lp-color-text-secondary);
}

.values-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
}

.value-card {
  position: relative;
  padding: 2.5rem;
  background: var(--lp-color-bg-elevated);
  border: 1px solid var(--lp-color-border);
  border-radius: var(--radius-lg);
  overflow: hidden;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.value-card:hover {
  transform: translateY(-8px);
  border-color: rgba(245, 158, 11, 0.3);
  box-shadow: light-dark(
    0 20px 60px rgba(245, 158, 11, 0.15),
    0 20px 60px rgba(245, 158, 11, 0.1)
  );
}

.card-number {
  font-family: 'Outfit', sans-serif;
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
  color: var(--lp-color-text-muted);
  margin-bottom: 1.5rem;
  letter-spacing: 0.1em;
}

.card-icon {
  font-size: var(--font-size-3xl);
  margin-bottom: 1.5rem;
}

.card-title {
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-bold);
  margin-bottom: 1rem;
}

.card-description {
  font-size: var(--font-size-base);
  color: var(--lp-color-text-secondary);
  line-height: 1.7;
}

.card-decoration {
  position: absolute;
  top: 0;
  right: 0;
  width: 150px;
  height: 150px;
  background: radial-gradient(circle at top right, rgba(245, 158, 11, 0.08) 0%, transparent 70%);
  pointer-events: none;
}

/* Features Section */
.features-section {
  padding: 8rem 2rem;
  background: var(--lp-color-bg-elevated);
  border-top: 1px solid var(--lp-color-border);
  border-bottom: 1px solid var(--lp-color-border);
  position: relative;
  z-index: 1;
}

.features-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 2rem;
}

.feature-card {
  display: flex;
  gap: 2rem;
  padding: 2rem;
  background: var(--lp-color-bg);
  border: 1px solid var(--lp-color-border);
  border-radius: var(--radius-lg);
  transition: all 0.4s;
}

.feature-card:hover {
  border-color: var(--lp-color-border-light);
}

.feature-visual {
  flex-shrink: 0;
  width: 120px;
  height: 120px;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.visual-bg {
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, rgba(245, 158, 11, 0.1) 0%, rgba(217, 119, 6, 0.05) 100%);
  border-radius: var(--radius-lg);
}

.visual-icon {
  font-size: var(--font-size-3xl);
  position: relative;
  z-index: 1;
}

.feature-content {
  flex: 1;
}

.feature-number {
  font-family: 'Outfit', sans-serif;
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
  color: var(--color-brand);
  letter-spacing: 0.1em;
  margin-bottom: 0.5rem;
}

.feature-title {
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-bold);
  margin-bottom: 0.5rem;
}

.feature-description {
  font-size: var(--font-size-sm);
  color: var(--lp-color-text-secondary);
  margin-bottom: 1rem;
}

.feature-points {
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.feature-points li {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: var(--font-size-sm);
  color: var(--lp-color-text-muted);
}

.feature-points svg {
  flex-shrink: 0;
  color: var(--color-brand);
}

/* Download Section */
.download-section {
  padding: 8rem 2rem;
  position: relative;
  z-index: 1;
}

.download-container {
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6rem;
  align-items: center;
}

.download-visual {
  position: relative;
  display: flex;
  justify-content: center;
}

.phone-mockup {
  position: relative;
  width: 280px;
  height: 560px;
  background: var(--lp-color-bg-card);
  border: 3px solid var(--lp-color-border-light);
  border-radius: 48px;
  padding: 8px;
  box-shadow: light-dark(
    0 40px 80px rgba(0, 0, 0, 0.15),
    0 40px 80px rgba(0, 0, 0, 0.4)
  );
}

.phone-screen {
  width: 100%;
  height: 100%;
  background: linear-gradient(180deg, var(--lp-color-bg) 0%, var(--lp-color-bg-elevated) 100%);
  border-radius: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.screen-content {
  text-align: center;
}

.app-icon {
  margin-bottom: 1.5rem;
}

.app-name {
  display: block;
  font-family: 'Outfit', sans-serif;
  font-size: var(--font-size-2xl);
  font-weight: var(--font-weight-bold);
  margin-bottom: 0.5rem;
}

.app-tagline {
  font-size: var(--font-size-sm);
  color: var(--lp-color-text-muted);
}

.phone-notch {
  position: absolute;
  top: 16px;
  left: 50%;
  transform: translateX(-50%);
  width: 100px;
  height: 28px;
  background: var(--lp-color-bg);
  border-radius: var(--radius-lg);
}

.download-decoration {
  position: absolute;
  inset: 0;
  pointer-events: none;
}

.deco-ring {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  border: 1px solid var(--lp-color-border);
  border-radius: 50%;
  opacity: 0.3;
}

.ring-1 {
  width: 350px;
  height: 350px;
  animation: ringPulse 4s ease-out infinite;
}

.ring-2 {
  width: 450px;
  height: 450px;
  animation: ringPulse 4s ease-out infinite 1s;
}

.ring-3 {
  width: 550px;
  height: 550px;
  animation: ringPulse 4s ease-out infinite 2s;
}

@keyframes ringPulse {
  0% { transform: translate(-50%, -50%) scale(0.8); opacity: 0.5; }
  100% { transform: translate(-50%, -50%) scale(1.2); opacity: 0; }
}

.download-content {
  max-width: 480px;
}

.download-label {
  font-family: 'Outfit', sans-serif;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  letter-spacing: 0.2em;
  color: var(--color-brand);
  margin-bottom: 1rem;
  display: block;
}

.download-title {
  font-size: var(--font-size-2xl);
  font-weight: var(--font-weight-extrabold);
  line-height: 1.2;
  margin-bottom: 1.5rem;
}

.download-title .highlight {
  background: linear-gradient(135deg, var(--color-brand) 0%, var(--color-brand-light) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.download-description {
  font-size: var(--font-size-base);
  color: var(--lp-color-text-secondary);
  line-height: 1.8;
  margin-bottom: 2.5rem;
}

.download-buttons {
  display: flex;
  gap: 1rem;
}

.store-btn {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.875rem 1.5rem;
  background: var(--lp-color-bg-card);
  border: 1px solid var(--lp-color-border);
  border-radius: var(--radius-md);
  color: var(--color-text-primary);
  text-decoration: none;
  transition: all 0.3s;
}

.store-btn:hover {
  background: var(--lp-color-bg-elevated);
  border-color: var(--lp-color-border-light);
  transform: translateY(-2px);
}

.store-text {
  display: flex;
  flex-direction: column;
  text-align: left;
}

.store-label {
  font-size: var(--font-size-xs);
  color: var(--lp-color-text-muted);
}

.store-name {
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-semibold);
}

/* Expert Section */
.expert-section {
  padding: 8rem 2rem;
  background: var(--lp-color-bg-elevated);
  border-top: 1px solid var(--lp-color-border);
  position: relative;
  z-index: 1;
}

.expert-container {
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6rem;
  align-items: center;
}

.expert-visual {
  position: relative;
}

.expert-image-frame {
  border-radius: var(--radius-lg);
  overflow: hidden;
  border: 1px solid var(--lp-color-border);
}

.image-placeholder {
  aspect-ratio: 4/3;
  background: linear-gradient(135deg, var(--lp-color-bg) 0%, var(--lp-color-bg-card) 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}

.placeholder-icon {
  font-size: 6rem;
  opacity: 0.2;
}

.placeholder-pattern {
  position: absolute;
  inset: 0;
  background-image: 
    linear-gradient(var(--lp-color-border) 1px, transparent 1px),
    linear-gradient(90deg, var(--lp-color-border) 1px, transparent 1px);
  background-size: 40px 40px;
  opacity: 0.3;
}

.expert-badge-float {
  position: absolute;
  bottom: -20px;
  right: 30px;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 1.5rem;
  background: var(--lp-color-bg-elevated);
  border: 1px solid var(--lp-color-border);
  border-radius: var(--radius-lg);
  box-shadow: light-dark(
    0 10px 40px rgba(0, 0, 0, 0.1),
    0 10px 40px rgba(0, 0, 0, 0.3)
  );
}

.badge-icon {
  font-size: var(--font-size-lg);
}

.badge-text {
  display: flex;
  flex-direction: column;
}

.badge-number {
  font-family: 'Outfit', sans-serif;
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-bold);
}

.badge-label {
  font-size: var(--font-size-xs);
  color: var(--lp-color-text-muted);
}

.expert-content {
  max-width: 480px;
}

.expert-label {
  font-family: 'Outfit', sans-serif;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  letter-spacing: 0.2em;
  color: var(--color-brand);
  margin-bottom: 1rem;
  display: block;
}

.expert-title {
  font-size: var(--font-size-2xl);
  font-weight: var(--font-weight-extrabold);
  line-height: 1.2;
  margin-bottom: 1.5rem;
}

.expert-title .highlight {
  background: linear-gradient(135deg, var(--color-brand) 0%, var(--color-brand-light) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.expert-description {
  font-size: var(--font-size-base);
  color: var(--lp-color-text-secondary);
  line-height: 1.8;
  margin-bottom: 2rem;
}

.expert-features {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 2.5rem;
}

.expert-feature {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: var(--lp-color-text-secondary);
}

.expert-feature svg {
  color: var(--color-brand);
}

.expert-cta {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--color-text-primary);
  text-decoration: none;
  font-weight: var(--font-weight-semibold);
  transition: all 0.3s;
}

.expert-cta:hover {
  color: var(--color-brand);
  gap: 1rem;
}

/* Footer */
.footer {
  background: var(--lp-color-bg);
  border-top: 1px solid var(--lp-color-border);
  padding: 5rem 2rem 2rem;
  position: relative;
  z-index: 1;
}

.footer-container {
  max-width: 1200px;
  margin: 0 auto;
}

.footer-top {
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: 4rem;
  margin-bottom: 4rem;
}

.footer-brand .logo {
  margin-bottom: 1rem;
}

.brand-tagline {
  font-size: var(--font-size-sm);
  color: var(--lp-color-text-muted);
}

.footer-links {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
}

.link-group h4 {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  margin-bottom: 1.5rem;
}

.link-group a {
  display: block;
  font-size: var(--font-size-sm);
  color: var(--lp-color-text-muted);
  text-decoration: none;
  margin-bottom: 0.75rem;
  transition: color 0.3s;
}

.link-group a:hover {
  color: var(--color-text-primary);
}

.footer-info {
  padding: 2rem 0;
  border-top: 1px solid var(--lp-color-border);
  border-bottom: 1px solid var(--lp-color-border);
  margin-bottom: 2rem;
}

.company-info p {
  font-size: var(--font-size-sm);
  color: var(--lp-color-text-muted);
  margin-bottom: 0.25rem;
}

.footer-bottom {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.copyright {
  font-size: var(--font-size-sm);
  color: var(--lp-color-text-muted);
}

.footer-legal {
  display: flex;
  gap: 1.5rem;
}

.footer-legal a {
  font-size: var(--font-size-sm);
  color: var(--lp-color-text-muted);
  text-decoration: none;
  transition: color 0.3s;
}

.footer-legal a:hover {
  color: var(--color-text-primary);
}

.gradient-text {
  background: linear-gradient(
    135deg,
    #6fb1fc 0%,
    #8f9dff 40%,
    #c7a7ff 70%,
    #ffd28f 100%
  );
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;

  font-weight: 700;
  letter-spacing: 0.02em;

  /* 은은한 깊이감 */
  text-shadow:
    0 0 12px rgba(143, 157, 255, 0.25),
    0 0 24px rgba(199, 167, 255, 0.15);
}

/* Responsive */
@media (max-width: 1024px) {
  .nav-menu {
    display: none;
  }
  
  .hero-container {
    grid-template-columns: 1fr;
    gap: 4rem;
  }
  
  .hero-visual {
    order: -1;
  }
  
  .hero-content {
    text-align: center;
    max-width: 100%;
  }
  
  .hero-actions {
    justify-content: center;
  }
  
  .hero-stats {
    justify-content: center;
  }
  
  .values-grid {
    grid-template-columns: 1fr;
  }
  
  .features-grid {
    grid-template-columns: 1fr;
  }
  
  .download-container {
    grid-template-columns: 1fr;
    gap: 4rem;
  }
  
  .download-content {
    text-align: center;
    max-width: 100%;
  }
  
  .download-buttons {
    justify-content: center;
  }
  
  .expert-container {
    grid-template-columns: 1fr;
    gap: 4rem;
  }
  
  .footer-top {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .hero {
    padding: 6rem 1.5rem 3rem;
  }
  
  .hero-title {
    font-size: var(--font-size-2xl);
  }
  
  .section-title {
    font-size: var(--font-size-2xl);
  }
  
  .app-preview {
    transform: none;
  }
  
  .floating-card {
    display: none;
  }
  
  .scroll-indicator {
    display: none;
  }
  
  .download-title,
  .expert-title {
    font-size: var(--font-size-xl);
  }
  
  .phone-mockup {
    width: 240px;
    height: 480px;
  }
  
  .feature-card {
    flex-direction: column;
    text-align: center;
  }
  
  .feature-visual {
    margin: 0 auto;
  }
  
  .feature-points {
    align-items: center;
  }
  
  .footer-links {
    grid-template-columns: 1fr 1fr;
  }
  
  .footer-bottom {
    flex-direction: column;
    gap: 1rem;
    text-align: center;
  }
}
</style>